// src/pages/sow-draft/useDraftInputPersistence.js
//
// Keeps the Draft SOW wizard's inputs across a refresh or a navigation
// away. Every field in step 2 lived in component state only, so reloading
// the page lost the project context, scope, assumptions, milestones and
// resource plan -- in practice the most typing in the application.
//
// Scoped per opportunity, because the wizard is always "a draft for one
// opportunity" and switching between them must not bleed one engagement's
// scope into another's.
//
// This is the local half of the story. The backend already persists these
// answers (sow_drafts.tag_values_json) but does not return them yet --
// neither DraftDetailDto nor DraftDetailResponse exposes tag_values. Once
// it does, server state should win for a draft that exists, and this keeps
// covering the case the server cannot know about: inputs typed before the
// draft was ever created.

import { useCallback, useEffect, useRef } from "react";

// Bump when the persisted shape changes; an old payload is then ignored
// rather than spread into state with missing or renamed fields.
const SCHEMA_VERSION = 1;
const KEY_PREFIX = "sow-draft-inputs";

function storageKey(opportunityId) {
  return `${KEY_PREFIX}:v${SCHEMA_VERSION}:opp:${opportunityId}`;
}

/**
 * Read saved inputs for one opportunity, or null.
 *
 * Every access is guarded: localStorage throws on access in some private
 * modes and when site data is blocked, and a half-written payload should
 * degrade to "nothing saved" rather than break the wizard.
 */
export function readSavedInputs(opportunityId) {
  if (!opportunityId) return null;
  try {
    const raw = window.localStorage.getItem(storageKey(opportunityId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== SCHEMA_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearSavedInputs(opportunityId) {
  if (!opportunityId) return;
  try {
    window.localStorage.removeItem(storageKey(opportunityId));
  } catch {
    /* nothing to clean up if storage is unavailable */
  }
}

/**
 * Persist `projectDetails` + `templateId` for `opportunityId`.
 *
 * Writes on change rather than on an interval, so a browser crash loses at
 * most the keystroke in flight. The payload is small (text fields and two
 * small arrays) -- the parsed proposal text is deliberately NOT included,
 * since it can run to hundreds of KB of extracted document content and
 * would risk the 5MB origin quota for something the user can re-upload.
 *
 * Returns { restore, clear } for the wizard to call on opportunity select
 * and after a successful create.
 */
export function useDraftInputPersistence({ opportunityId, projectDetails, templateId }) {
  // Skip the write that fires immediately after a restore, which would
  // otherwise rewrite identical data on every mount.
  const hydratedFor = useRef(null);

  useEffect(() => {
    if (!opportunityId) return;
    if (hydratedFor.current !== opportunityId) {
      hydratedFor.current = opportunityId;
      return;
    }
    try {
      window.localStorage.setItem(
        storageKey(opportunityId),
        JSON.stringify({
          version: SCHEMA_VERSION,
          savedAt: new Date().toISOString(),
          projectDetails,
          templateId,
        })
      );
    } catch {
      // Quota exceeded or storage blocked. Losing persistence is not worth
      // failing the wizard over, and the inputs are still in React state.
    }
  }, [opportunityId, projectDetails, templateId]);

  const restore = useCallback((id) => readSavedInputs(id), []);
  const clear = useCallback((id) => clearSavedInputs(id), []);

  return { restore, clear };
}
